import { Skeleton } from "@mui/material";
import { useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  DataGrid,
  Dialog,
  DialogContent,
  Button,
} from "../../../components";
import { formatDate, formatPrice } from "../../../utils";
import { format } from "date-fns";

export default function PainelAnaliseFiscalDistribuicao({
  loading,
  resumoFiscal = {},
}) {
  const [openSemDocumentoDialog, setOpenSemDocumentoDialog] = useState(false);
  const [semDocumentoData, setSemDocumentoData] = useState({
    nomePagamento: "",
    total: 0,
    notas: [],
  });
  const semDocumentoColunas = [
    {
      field: "serieDocumento",
      headerName: "Série",
      flex: 5,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "numeroDocumento",
      headerName: "Número",
      flex: 5,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "cliente",
      headerName: "Cliente",
      flex: 15,
      minWidth: 120,
    },
    {
      field: "dataEmissao",
      headerName: "Emissão",
      flex: 6,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      valueGetter: (params) =>
        format(formatDate.received(params.value), "dd/MM/yyyy"),
    },
    {
      field: "valorTotal",
      headerName: "Valor",
      flex: 6,
      minWidth: 120,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) => formatPrice(params.value),
    },
  ];

  const distribuicao = [
    {
      name: "Autorizadas",
      value: resumoFiscal.notasAutorizadas ?? 0,
      color: "#2e7d32",
    },
    {
      name: "Rejeitadas",
      value: resumoFiscal.notasRejeitadas ?? 0,
      color: "#d32f2f",
    },
    {
      name: "Inutilizadas",
      value: resumoFiscal.notasInutilizadas ?? 0,
      color: "#616161",
    },
    {
      name: "Contingência",
      value: resumoFiscal.notasContingencia ?? 0,
      color: "#ffb300",
    },
    {
      name: "Pendentes",
      value: resumoFiscal.notasPendentes ?? 0,
      color: "#ff8f00",
    },
    {
      name: "Canceladas",
      value: resumoFiscal.notasCanceladas ?? 0,
      color: "#8e24aa",
    },
    {
      name: "Sem documento",
      value: resumoFiscal.semDocumentoFiscal ?? 0,
      color: "#fb8c00",
    },
  ];

  const total = distribuicao.reduce((s, it) => s + (it.value || 0), 0);

  const chartOptions = useMemo(() => {
    const data = distribuicao.map((it) => ({
      name: it.name,
      y: it.value || 0,
      color: it.color,
    }));

    return {
      chart: {
        type: "pie",
        backgroundColor: "transparent",
        height: 220,
      },
      title: { text: null },
      tooltip: {
        pointFormat: "<b>{point.y}</b> ({point.percentage:.1f}%)",
      },
      plotOptions: {
        pie: {
          innerSize: "70%",
          dataLabels: { enabled: false },
          borderWidth: 0,
        },
      },
      credits: { enabled: false },
      series: [
        {
          name: "Distribuição",
          colorByPoint: true,
          data,
        },
      ],
    };
  }, [distribuicao]);

  const pagamentosRaw = resumoFiscal.distribuicaoMeioPagamento || [];
  const pagamentos = (pagamentosRaw || []).map((p) => {
    const total = Number(p.total || 0);
    const notasObj = p.notas || {};
    const com = Number(notasObj?.NF?.total || 0);
    const sem = Number(notasObj?.["01"]?.total || 0);
    const notasCom = notasObj?.NF?.notas || [];
    const notasSem = notasObj?.["01"]?.notas || [];
    return {
      descricao: p.meioPagamento || "—",
      total,
      color: p.color,
      com,
      sem,
      notasCom,
      notasSem,
    };
  });

  const maxPagamento =
    pagamentos.reduce((m, it) => Math.max(m, it.total || 0), 0) || 1;

  function handleSemDocumentoClick(pagamento) {
    if (!pagamento.notasSem || pagamento.notasSem.length === 0) return;
    setSemDocumentoData({
      nomePagamento: pagamento.descricao,
      total: pagamento.total,
      notas: pagamento.notasSem,
    });
    setOpenSemDocumentoDialog(true);
  }

  return (
    <>
      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: 3,
              border: "1px solid #f0f0f0",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                boxSizing: "border-box",
              }}
            >
              <div>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Distribuição por situação
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Participação de cada status sobre os documentos enviados
                </Typography>
              </div>

              {loading ? (
                <Box sx={{ flex: 1, mt: 2 }}>
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton
                      key={i}
                      variant="rectangular"
                      height={40}
                      sx={{ mb: 1, borderRadius: 1 }}
                    />
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mt: 2,
                    flex: 1,
                    minHeight: 0,
                  }}
                >
                  <Box
                    sx={{
                      flex: "0 0 auto",
                      width: { xs: "40%", sm: "45%" },
                      textAlign: "center",
                    }}
                  >
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={chartOptions}
                      containerProps={{ style: { width: "100%" } }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 1 }}
                    >
                      Total de documentos enviados
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {total || 0} notas
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1, pl: 2, overflow: "auto" }}>
                    {distribuicao.map((item) => {
                      const value = item.count || item.value || 0;
                      const percent = total
                        ? ((value / total) * 100).toFixed(1)
                        : "0.0";
                      return (
                        <Box
                          key={item.name}
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              flexShrink: 0,
                              borderRadius: "50%",
                              backgroundColor: item.color || "#1976d2",
                              mr: 1.5,
                            }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {item.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                              }}
                            >
                              {value} notas ({percent}%)
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: 3,
              border: "1px solid #f0f0f0",
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <div>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Distribuição por meio de pagamento
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Vendas realizadas por forma de recebimento
                </Typography>
              </div>

              {loading ? (
                <Box sx={{ mt: 2 }}>
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton
                      key={i}
                      variant="rectangular"
                      height={40}
                      sx={{ mb: 1, borderRadius: 1 }}
                    />
                  ))}
                </Box>
              ) : (
                <Box sx={{ mt: 2 }}>
                  {pagamentos
                    .sort((a, b) => b.total - a.total)
                    .map((p, idx) => {
                      const outerPercent = Math.round(
                        (p.total / maxPagamento) * 100,
                      );
                      const comPercent = p.total
                        ? Math.round((p.com / p.total) * 100)
                        : 0;
                      const semPercent = p.total ? 100 - comPercent : 0;
                      return (
                        <Box key={p.descricao + idx} sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {p.descricao}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {formatPrice(p.total)}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Box
                                sx={{
                                  background: "#e9eef6",
                                  height: 14,
                                  borderRadius: 2,
                                  overflow: "hidden",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${outerPercent}%`,
                                    display: "flex",
                                    height: "100%",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: `${comPercent}%`,
                                      background: "#2e86ff",
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      width: `${semPercent}%`,
                                      background: "#f6a623",
                                    }}
                                  />
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mt: 0.5,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: "text.secondary" }}
                                >
                                  Com documento: {formatPrice(p.com)}
                                </Typography>
                                <Typography
                                  onClick={() => handleSemDocumentoClick(p)}
                                  variant="caption"
                                  sx={{
                                    cursor: "pointer",
                                    transition:
                                      "color 0.3s, text-decoration 0.2s",
                                    color: "text.secondary",
                                    "&:hover": {
                                      color: "#f6a623",
                                      textDecoration: "underline",
                                    },
                                  }}
                                >
                                  Sem documento: {formatPrice(p.sem)}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}

                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                      mt: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: "#2e86ff",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        Com documento
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: "#f6a623",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        Sem documento
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Dialog
        fullWidth
        maxWidth="md"
        PaperComponent="div"
        PaperProps={{
          style: {
            borderRadius: "0.7rem",
            background: "white",
          },
        }}
        open={openSemDocumentoDialog}
        onClose={() => setOpenSemDocumentoDialog(false)}
      >
        <DialogContent>
          <Box className="mb-3 d-flex align-items-center justify-content-between">
            <Box>
              <h6 className="text-uppercase fw-bold">
                Documentos sem nota fiscal — {semDocumentoData.nomePagamento}
              </h6>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Recebimentos identificados sem documento fiscal vinculado na
                competência.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="padrao"
              disableElevation
              onClick={() => setOpenSemDocumentoDialog(false)}
              style={{
                minWidth: 38,
                paddingInline: 10,
                borderRadius: 8,
              }}
            >
              <i className="ph-bold ph-x" style={{ fontSize: 15 }} />
            </Button>
          </Box>
          <DataGrid
            rows={semDocumentoData.notas}
            columns={semDocumentoColunas}
            rowCount={semDocumentoData.notas?.length || 0}
            disableAcoes
          />
          <Box className="d-flex justify-content-between mt-3">
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {semDocumentoData.notas?.length || 0} Documento(s)
            </Typography>
            <Typography
              variant="h6"
              className="text-uppercase fw-bold"
              style={{ color: "#f6a623" }}
            >
              {formatPrice(semDocumentoData.total)}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
